import React, {useCallback, useEffect, useRef, useState} from "react";
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import {Box, Chip, Paper, Tab, Tabs, Typography} from "@mui/material";
import {io} from "socket.io-client";
import {getRequest, server} from "../config";

const center = {lat: 20.5937, lng: 78.9629};
const CUSTOMER_ICON = "https://maps.google.com/mapfiles/ms/icons/blue-dot.png";
const PARTNER_ICON = "https://maps.google.com/mapfiles/ms/icons/orange-dot.png";
const PARTNER_SERVICE_ICON = "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
const STALE_AFTER_MS = 60_000;

const mapContainerStyle = {width: "100%", height: "calc(100vh - 205px)", minHeight: 420};

const PartnerMap = () => {
  const [view, setView] = useState("live");
  const [partnerList, setPartnerList] = useState([]);
  const [liveBookings, setLiveBookings] = useState([]);
  const [connectionState, setConnectionState] = useState("connecting");
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [now, setNow] = useState(Date.now());
  const mapRef = useRef(null);

  const {isLoaded, loadError} = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyA4ayX0TFGnC7CstosUjDgJawsYfMAZUFE",
  });

  const fetchPartners = useCallback(async () => {
    try {
      const uri = `${server.uri}services?filter=${encodeURIComponent(JSON.stringify({
        include: [{relation: "partner", scope: {fields: {name: true}}}],
      }))}`;
      const services = await getRequest(uri);
      setPartnerList((services || []).filter((item) => item?.location && item?.partner));
    } catch (error) {
      console.error("Unable to load partner service locations", error);
    }
  }, []);

  useEffect(() => {
    if (view === "partners") void fetchPartners();
  }, [view, fetchPartners]);

  useEffect(() => {
    const expiryTimer = setInterval(() => setNow(Date.now()), 10000);
    if (view !== "live") {
      setConnectionState("disconnected");
      return () => clearInterval(expiryTimer);
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      setConnectionState("admin sign-in required");
      return () => clearInterval(expiryTimer);
    }

    const socket = io(server.uri, {auth: {token}, timeout: 10000});
    setConnectionState("connecting");
    socket.on("connect", () => {
      setConnectionState("live");
      socket.emit("live-location:admin:subscribe", (result) => {
        if (!result?.ok) {
          setConnectionState(result?.error || "access denied");
          return;
        }
        setLiveBookings(result.bookings || []);
      });
    });
    socket.on("disconnect", () => setConnectionState("reconnecting"));
    socket.on("connect_error", (error) => setConnectionState(error.message || "connection failed"));
    socket.on("live-location:booking", (booking) => {
      setLiveBookings((current) => {
        const existing = current.find((item) => item.serviceRequestId === booking.serviceRequestId);
        const merged = {
          ...existing,
          ...booking,
          locations: {...(existing?.locations || {}), ...(booking.locations || {})},
        };
        return existing
          ? current.map((item) => item.serviceRequestId === merged.serviceRequestId ? merged : item)
          : [merged, ...current];
      });
    });
    socket.on("live-location:update", (event) => {
      setLiveBookings((current) => current.map((booking) => {
        if (booking.serviceRequestId !== event.serviceRequestId) return booking;
        return {
          ...booking,
          locations: {...(booking.locations || {}), [event.role]: event.location},
        };
      }));
    });
    socket.on("live-location:ended", ({serviceRequestId}) => {
      setLiveBookings((current) => current.filter((booking) => booking.serviceRequestId !== serviceRequestId));
      setSelectedBookingId((current) => current === serviceRequestId ? null : current);
    });

    return () => {
      clearInterval(expiryTimer);
      socket.disconnect();
    };
  }, [view]);

  const locationIsFresh = (location) =>
    Boolean(location?.updatedAt && now - Date.parse(location.updatedAt) <= STALE_AFTER_MS);

  const focusBooking = (booking) => {
    setSelectedBookingId(booking.serviceRequestId);
    const points = [booking.locations?.customer, booking.locations?.partner]
      .filter(locationIsFresh)
      .map((location) => ({lat: location.latitude, lng: location.longitude}));
    if (!mapRef.current || !points.length) return;
    if (points.length === 1) {
      mapRef.current.panTo(points[0]);
      mapRef.current.setZoom(15);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    mapRef.current.fitBounds(bounds, 70);
  };

  if (loadError) return <div>Unable to load the map.</div>;
  if (!isLoaded) return <div>Loading map…</div>;

  return (
    <Box sx={{minWidth: 0}}>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5}}>
        <Tabs value={view} onChange={(_, value) => setView(value)} aria-label="Map view">
          <Tab value="live" label="Live bookings" />
          <Tab value="partners" label="Partner service locations" />
        </Tabs>
        {view === "live" && (
          <Chip
            size="small"
            color={connectionState === "live" ? "success" : "default"}
            label={`${connectionState} · ${liveBookings.length} ongoing`}
          />
        )}
      </Box>

      <Box sx={{display: "grid", gridTemplateColumns: view === "live" ? "minmax(250px, 320px) minmax(0, 1fr)" : "1fr", gap: 1.5}}>
        {view === "live" && (
          <Paper variant="outlined" sx={{height: mapContainerStyle.height, minHeight: mapContainerStyle.minHeight, overflow: "auto", p: 1}}>
            {liveBookings.length === 0 ? (
              <Typography color="text.secondary" sx={{p: 2, fontSize: 13}}>
                {connectionState === "live" ? "No ongoing bookings." : "Waiting for live connection…"}
              </Typography>
            ) : liveBookings.map((booking) => {
              const customerFresh = locationIsFresh(booking.locations?.customer);
              const partnerFresh = locationIsFresh(booking.locations?.partner);
              return (
                <Box
                  key={booking.serviceRequestId}
                  onClick={() => focusBooking(booking)}
                  sx={{p: 1.25, mb: 1, border: "1px solid", borderColor: selectedBookingId === booking.serviceRequestId ? "success.main" : "divider", borderRadius: 1, cursor: "pointer", "&:hover": {bgcolor: "action.hover"}}}
                >
                  <Typography sx={{fontSize: 13, fontWeight: 700}}>{booking.serviceName || "Service booking"}</Typography>
                  <Typography color="text.secondary" sx={{fontSize: 12, mt: 0.25}}>
                    {booking.customerName || "Customer"} · {booking.partnerName || "Partner"}
                  </Typography>
                  <Typography color="text.secondary" sx={{fontSize: 11, mt: 0.25}} noWrap>
                    {booking.bookingId || booking.address || booking.serviceRequestId}
                  </Typography>
                  <Box sx={{display: "flex", gap: 1.5, mt: 0.75}}>
                    <Typography sx={{fontSize: 11, color: customerFresh ? "#2869bc" : "text.disabled"}}>
                      Customer {customerFresh ? "live" : "waiting"}
                    </Typography>
                    <Typography sx={{fontSize: 11, color: partnerFresh ? "#df8b21" : "text.disabled"}}>
                      Partner {partnerFresh ? "live" : "waiting"}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Paper>
        )}

        <Box sx={{minWidth: 0, overflow: "hidden", border: "1px solid #dce5e4", borderRadius: 1}}>
          <GoogleMap
            mapContainerStyle={{width: "100%", height: mapContainerStyle.height, minHeight: mapContainerStyle.minHeight}}
            center={center}
            zoom={5}
            onLoad={(map) => { mapRef.current = map; }}
            onUnmount={() => { mapRef.current = null; }}
          >
            {view === "partners" && partnerList.map((item) => (
              <Marker
                key={item.serviceId || item.partner?.userId}
                icon={{url: PARTNER_SERVICE_ICON, scaledSize: new window.google.maps.Size(36, 36)}}
                title={item.partner?.name}
                position={{lat: Number(item.location.lat), lng: Number(item.location.lng)}}
              />
            ))}
            {view === "live" && liveBookings.map((booking) => {
              const customer = locationIsFresh(booking.locations?.customer) ? booking.locations.customer : null;
              const partner = locationIsFresh(booking.locations?.partner) ? booking.locations.partner : null;
              const path = [customer, partner].filter(Boolean).map((location) => ({lat: location.latitude, lng: location.longitude}));
              return (
                <React.Fragment key={booking.serviceRequestId}>
                  {customer && <Marker position={path[0]} icon={{url: CUSTOMER_ICON, scaledSize: new window.google.maps.Size(38, 38)}} title={`Customer · ${booking.customerName || ""}`} />}
                  {partner && <Marker position={partner ? {lat: partner.latitude, lng: partner.longitude} : path[0]} icon={{url: PARTNER_ICON, scaledSize: new window.google.maps.Size(38, 38)}} title={`Partner · ${booking.partnerName || ""}`} />}
                  {path.length === 2 && <Polyline path={path} options={{strokeColor: "#087f70", strokeOpacity: 0.7, strokeWeight: 3}} />}
                </React.Fragment>
              );
            })}
          </GoogleMap>
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerMap;